<template>
  <div class="min-h-screen bg-gray-50">

    <!-- Back nav + Export PDF -->
    <div class="bg-white border-b border-gray-100 sticky top-0 z-10 no-print">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 py-3 tw-flex items-center justify-between gap-4">
        <router-link
          to="/profile/update#personal-home"
          class="tw-flex items-center gap-1.5 text-sm text-gray-500 no-underline hover:text-gray-800 transition-colors"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
          </svg>
          Home
        </router-link>
        <div v-if="deepDiveData" class="tw-flex items-center gap-2">
          <button
            @click="exportPDF"
            class="tw-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-800 transition-colors bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
            </svg>
            Export PDF
          </button>

          <button
            v-if="deepDiveData.has_pptx"
            @click="downloadSlideDeck"
            :disabled="downloadingSlide"
            class="tw-flex items-center gap-1.5 text-xs font-medium text-gray-700 hover:text-gray-900 transition-colors border border-gray-200 bg-white hover:bg-gray-50 px-3 py-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
            </svg>
            {{ downloadingSlide ? '…' : $t('trp_download_presentation') }}
          </button>

          <button
            v-if="deepDiveData.has_pdf && isPaidSubscriber"
            @click="downloadFullPdf"
            :disabled="downloadingPdf"
            class="tw-flex items-center gap-1.5 text-xs font-medium text-white bg-primary hover:opacity-90 transition-opacity px-3 py-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
            </svg>
            {{ downloadingPdf ? '…' : $t('trp_download_full_report') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="max-w-4xl mx-auto px-4 sm:px-6 py-16 tw-flex justify-center">
      <svg class="animate-spin h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
      </svg>
    </div>

    <div v-else class="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">

      <!-- Header: title + year dropdown -->
      <div class="tw-flex items-end justify-between gap-4 flex-wrap">
        <h1 class="text-2xl font-bold text-gray-900 m-0">{{ $t('trp_entry_deepdive_title') }}</h1>
        <select
          v-if="availableYears.length > 0"
          class="no-print text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-700 bg-white focus:outline-none"
          :value="selectedYear"
          @change="$router.push('/deep-dive-reports/' + $event.target.value)"
          :aria-label="$t('trp_period_select')"
        >
          <option v-for="item in availableYears" :key="item.year" :value="item.year">
            {{ item.year }}
          </option>
        </select>
      </div>

      <!-- Empty state: no reports at all -->
      <div v-if="availableYears.length === 0" class="py-20 text-center text-gray-400">
        {{ $t('trp_not_available') }}
      </div>

      <!-- Empty state: no report for selected year -->
      <div v-else-if="!deepDiveData" class="py-20 text-center text-gray-400">
        {{ $t('trp_not_available') }}
      </div>

      <template v-else>

        <!-- Executive Summary -->
        <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-6 space-y-4">
          <p class="text-[11px] font-semibold uppercase tracking-widest text-gray-400 m-0">{{ $t('trp_executive_summary') }}</p>
          <div
            v-if="markedSummary"
            class="prose prose-sm max-w-none text-gray-700"
            v-html="markedSummary"
          />
          <p v-else class="text-sm text-gray-400 m-0 italic">{{ $t('trp_no_summary') }}</p>
        </div>

        <!-- Download error -->
        <div v-if="downloadError" class="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 no-print">
          {{ downloadError }}
        </div>

      </template>
    </div>
  </div>
</template>

<script>
  import { fetchDeepDiveReports, fetchDeepDiveReport, getDeepDiveFullPdfUrl, getDeepDiveSlideDeckUrl } from '@/api/deep-dive-reports'
  import { marked } from 'marked'

  export default {
    name: 'DeepDiveReportDetail',

    data () {
      return {
        isLoading: true,
        availableYears: [],
        selectedYear: null,
        deepDiveData: null,
        downloadingPdf: false,
        downloadingSlide: false,
        downloadError: null,
      }
    },

    computed: {
      isPaidSubscriber () {
        return this.$store.getters.hasTransitionReportPaidAccess
      },
      markedSummary () {
        if (!this.deepDiveData?.executive_summary) return null
        return marked.parse(this.deepDiveData.executive_summary)
      },
    },

    methods: {
      async loadAvailableYears () {
        try {
          this.availableYears = await fetchDeepDiveReports()
        } catch (e) {
          this.availableYears = []
        }
      },
      async loadDetail (year) {
        this.isLoading = true
        this.deepDiveData = null
        this.downloadError = null
        try {
          this.deepDiveData = await fetchDeepDiveReport(year)
        } catch (e) {
          this.deepDiveData = null
        } finally {
          this.isLoading = false
        }
      },
      async downloadFullPdf () {
        this.downloadingPdf = true
        this.downloadError = null
        try {
          const response = await getDeepDiveFullPdfUrl(this.selectedYear)
          window.open(response.url, '_blank', 'noopener')
        } catch (e) {
          this.downloadError = 'Could not download the report. Please try again.'
        } finally {
          this.downloadingPdf = false
        }
      },
      async downloadSlideDeck () {
        this.downloadingSlide = true
        this.downloadError = null
        try {
          const response = await getDeepDiveSlideDeckUrl(this.selectedYear)
          window.open(response.url, '_blank', 'noopener')
        } catch (e) {
          this.downloadError = 'Could not download the presentation. Please try again.'
        } finally {
          this.downloadingSlide = false
        }
      },
      async exportPDF () {
        const html2pdf = (await import('html2pdf.js')).default
        await html2pdf().set({
          margin: [12, 14],
          filename: `deep-dive-report-${this.selectedYear}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'legacy'] },
        }).from(this.buildReportHTML(), 'string').save()
      },
      buildReportHTML () {
        const summary = this.deepDiveData?.executive_summary
          ? marked.parse(this.deepDiveData.executive_summary)
          : ''
        return `<div style="width:780px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#1f2937;line-height:1.6;background:#fff;padding:32px 28px 60px;">
          <div style="display:flex;justify-content:space-between;align-items:baseline;border-bottom:2px solid #1f2937;padding-bottom:12px;margin-bottom:32px;">
            <div style="font-size:22px;font-weight:700;">Deep Dive Report</div>
            <div style="font-size:12px;color:#6b7280;text-transform:uppercase;letter-spacing:0.08em;">${this.selectedYear}</div>
          </div>
          <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#6b7280;margin-bottom:12px;">Executive Summary</div>
          <div style="font-size:13px;color:#374151;line-height:1.7;">${summary}</div>
        </div>`
      },
    },

    watch: {
      '$route.params.year': {
        async handler (year) {
          if (year && Number(year) !== this.selectedYear) {
            this.selectedYear = Number(year)
            await this.loadDetail(this.selectedYear)
          }
        },
        immediate: false,
      },
    },

    async mounted () {
      await this.loadAvailableYears()

      const paramYear = this.$route.params.year ? Number(this.$route.params.year) : null
      this.selectedYear = paramYear || (this.availableYears[0]?.year ?? null)

      if (this.selectedYear) {
        await this.loadDetail(this.selectedYear)
      } else {
        this.isLoading = false
      }
    },
  }
</script>
